import { Checkbox, ConfigProvider, Input, InputRef } from 'antd';
import { ReactNode, memo, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import Task from '../../../types/Task';
import { Epic } from '../../../types/Epic';
import { TaskDependencyEdge } from '../../../types/TaskDependency';
import { TaskType } from '../../../types/TaskType';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import colorTokens from '../../../constants/design_system/colors';
import BaseDropdown from './BaseDropdown';
import { TaskTypeLabel } from './TaskTypeDropdown';
import NoDragWrapper from './NoDragWrapper';
import {
  DependencyRow,
  HEADER_ROW_HEIGHT,
  TASK_ROW_HEIGHT,
  buildOrderedGroups,
  buildRows,
  rowHeight,
} from './taskDependencyOptions';

export type TaskDependencyDropdownProps = {
  options: Task[];
  epics: Epic[];
  /** Task ids hidden entirely from the list (self + tasks linked in the opposite direction). */
  excludeTaskIds: number[];
  /** Edges already linked in THIS direction — rendered checked; clicking one removes it. */
  selectedEdges: TaskDependencyEdge[];
  onAdd: (task: Task) => void;
  onRemove: (dependencyId: number, relatedTaskId: number) => void;
  /** Clears every link in this direction at once; the row is hidden when nothing is linked. */
  onRemoveAll?: () => void;
  /** Copy for the remove-all row, e.g. "Remove All Blockers". */
  removeAllLabel?: string;
  trigger: ReactNode;
  placeholder?: string;
  /** Open immediately (used by the table cell when entering edit mode). */
  defaultOpen?: boolean;
  /** Called when the dropdown closes (lets the table cell leave edit mode). */
  onClose?: () => void;
};

const TaskDependencyDropdown = (props: TaskDependencyDropdownProps) => {
  const [isOpen, setIsOpen] = useState(!!props.defaultOpen);
  const [searchTerm, setSearchTerm] = useState('');
  // Bumped on every open. Used as the option list's key so each open mounts a brand-new
  // scroll container (and virtualizer), which is what actually resets the scroll offset:
  // AntD keeps the popup subtree alive while hidden, so `{isOpen && …}` alone does not
  // reliably tear the list down on close. Mirrors ParentTask / Epic dropdowns. (#2600)
  const [scrollKey, setScrollKey] = useState(0);
  const searchInputRef = useRef<InputRef>(null);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  // Map currently-linked tasks (this direction) to their edge so a click can remove them.
  const edgeByTaskId = useMemo(() => new Map(props.selectedEdges.map((e) => [e.task.id, e])), [props.selectedEdges]);

  // Always holds the latest selection/exclusion AND the latest options/epics, but is
  // deliberately NOT a dependency of the ordering memo below — that is what keeps the order
  // frozen while the dropdown is open. options/epics must be read through this ref rather
  // than as memo deps because toggling a dependency invalidates the workspace tasks query,
  // which hands this component a brand-new options array mid-open; depending on that identity
  // recomputed the order under the user's cursor, which is why the earlier freeze bounced. (#2601)
  const latest = useRef({
    edgeByTaskId,
    excludeTaskIds: props.excludeTaskIds,
    options: props.options,
    epics: props.epics,
  });
  latest.current = { edgeByTaskId, excludeTaskIds: props.excludeTaskIds, options: props.options, epics: props.epics };

  // Recomputed only on the open transition, mirroring the Labels dropdown: checking a task
  // must not make it jump to the top mid-click, even if options/epics change reference while
  // open. (#2601) Skipping the work entirely while closed also keeps large workspaces cheap. (#2625)
  const orderedGroups = useMemo(() => {
    if (!isOpen) return [];
    const { edgeByTaskId: selected, excludeTaskIds, options, epics } = latest.current;
    return buildOrderedGroups({
      options,
      epics,
      selectedIds: new Set(selected.keys()),
      excludeIds: new Set(excludeTaskIds),
    });
  }, [isOpen]);

  const rows = useMemo(() => buildRows(orderedGroups, searchTerm), [orderedGroups, searchTerm]);

  // Toggle membership and keep the dropdown open so several links can be set at once.
  const onToggle = (task: Task) => {
    const edge = edgeByTaskId.get(task.id);
    if (edge) props.onRemove(edge.id, task.id);
    else props.onAdd(task);
  };

  return (
    <NoDragWrapper className="h-full w-full min-w-0">
      <BaseDropdown
        disableTransition
        trigger={['click']}
        open={isOpen}
        onOpenChange={(open) => {
          if (open) setScrollKey((k) => k + 1);
          setIsOpen(open);
          if (!open) {
            setSearchTerm('');
            props.onClose?.();
          }
        }}
        dropdownRender={() => (
          <div className="flex flex-col items-start justify-start bg-brand-white shadow-md rounded-lg px-2 pb-2 min-w-[216px]">
            <div className="w-full mb-2 bg-brand-white pt-2">
              <Input
                ref={searchInputRef}
                placeholder={props.placeholder ?? 'Search for Epic or Task...'}
                prefix={<SearchOutlined className="mr-1" />}
                allowClear={searchTerm.length > 0}
                className="[&_input::placeholder]:!text-neutral-900 px-2 py-1 text-neutral-900 w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                size="small"
              />
            </div>

            {/* Mounted only while open, and re-keyed on every open so reopening always starts
                scrolled to the top even when AntD keeps the popup subtree alive. (#2600) */}
            {isOpen && (
              <DependencyOptionList
                key={scrollKey}
                rows={rows}
                searchTerm={searchTerm}
                isSelected={(taskId) => edgeByTaskId.has(taskId)}
                onToggle={onToggle}
              />
            )}

            {/* Clears every link at once — a checkbox still toggles just one. Pinned to the
                bottom like "Remove Parent" so it's reachable without scrolling. (#2599) */}
            {props.onRemoveAll && props.selectedEdges.length > 0 && !searchTerm.trim() && (
              <div className="w-full sticky bottom-0 bg-brand-white pt-1">
                <div className="w-full h-px bg-neutral-300 mb-1" />
                <div
                  role="button"
                  className="w-[200px] p-1 flex items-center gap-2 cursor-pointer hover:bg-neutral-200"
                  onClick={() => props.onRemoveAll?.()}
                >
                  <CloseOutlined className="text-xs text-neutral-900" />
                  <span className="text-neutral-900 text-sm font-normal leading-tight">
                    {props.removeAllLabel ?? 'Remove All'}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      >
        <div className="h-full w-full min-w-0 flex items-center cursor-pointer">{props.trigger}</div>
      </BaseDropdown>
    </NoDragWrapper>
  );
};

/**
 * Virtualized option list. Only the visible rows are mounted, so a workspace with a few
 * thousand tasks opens and reacts as fast as a small one. (#2625)
 */
const DependencyOptionList = (props: {
  rows: DependencyRow[];
  searchTerm: string;
  isSelected: (taskId: number) => boolean;
  onToggle: (task: Task) => void;
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: props.rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => rowHeight(props.rows[index]),
    overscan: 10,
  });

  // A new search term produces a different list; keep the user at the top of the results.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [props.searchTerm]);

  if (props.rows.length === 0) {
    return <div className="justify-start text-neutral-900 text-xs font-semibold py-1">No Results</div>;
  }

  return (
    <div ref={scrollRef} data-testid="dependency-option-list" className="w-full max-h-[400px] overflow-y-auto">
      <div className="relative w-full" style={{ height: `${virtualizer.getTotalSize()}px` }}>
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const row = props.rows[virtualRow.index];
          return (
            <div
              key={row.key}
              className="absolute top-0 left-0 w-full"
              style={{ height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)` }}
            >
              {row.kind === 'header' ? (
                <div
                  className="flex items-center text-neutral-900 text-xs font-semibold w-full max-w-[180px] truncate overflow-hidden whitespace-nowrap"
                  style={{ height: `${HEADER_ROW_HEIGHT}px` }}
                  title={row.title}
                >
                  {row.title}
                </div>
              ) : (
                <SelectableTaskItem
                  task={row.task}
                  isSelected={props.isSelected(row.task.id)}
                  onClick={props.onToggle}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// A task row with a checkbox reflecting its linked state (mirrors Labels' SelectableLabelItem).
// Memoized so toggling one task doesn't re-render every other mounted row. (#2625)
const SelectableTaskItem = memo((props: { task: Task; isSelected: boolean; onClick: (task: Task) => void }) => {
  return (
    <div
      className="w-[200px] px-1 flex items-center justify-start gap-2 cursor-pointer hover:bg-neutral-200"
      style={{ height: `${TASK_ROW_HEIGHT}px` }}
      onClick={() => props.onClick(props.task)}
    >
      <ConfigProvider
        theme={{
          components: {
            Checkbox: {
              colorPrimary: colorTokens.blue[600],
              colorPrimaryHover: colorTokens.blue[700],
            },
          },
        }}
      >
        <Checkbox checked={props.isSelected} />
      </ConfigProvider>
      <div className="flex items-center gap-1 min-w-0">
        <TaskTypeLabel type={props.task.type as TaskType} size="small" />
        <span
          className="text-neutral-900 text-sm font-normal leading-tight truncate overflow-hidden whitespace-nowrap"
          title={props.task.title}
        >
          {props.task.title}
        </span>
      </div>
    </div>
  );
});
SelectableTaskItem.displayName = 'SelectableTaskItem';

export default TaskDependencyDropdown;
